import { useLayoutEffect, useRef, useState } from 'react'

import type { QuantRunLogState } from '@/app/hooks/useQuantRunLog'
import { cn } from '@/ui/lib/utils'
import { QuantUnavailable } from '@/ui/QuantUnavailable'

const TOLERANCE = 24

export interface QuantLogPanelProps {
  log: QuantRunLogState & { retry: () => void }
  /** The accessible name of the log: `Run log` for a run, `Session log` for a live session. */
  ariaLabel: string
}

/**
 * The tailing log panel shared by the run detail (spec 036 D-4) and the live session detail
 * (spec 041): the Follow toggle, the Streaming / Stream closed indicator and the log itself.
 * Extracted verbatim from `QuantRunDetail`; only the accessible name is passed in.
 */
export function QuantLogPanel({ log, ariaLabel }: QuantLogPanelProps) {
  const [follow, setFollow] = useState(true)
  const preRef = useRef<HTMLPreElement>(null)
  // scrollTop we set ourselves, so its scroll event is not mistaken for the user.
  const ownTop = useRef(-1)

  function toBottom() {
    const el = preRef.current
    if (el === null) return
    el.scrollTop = el.scrollHeight
    ownTop.current = el.scrollTop
  }

  useLayoutEffect(() => {
    if (follow) toBottom()
  }, [follow, log.text])

  function onScroll() {
    const el = preRef.current
    if (el === null) return
    if (Math.abs(el.scrollTop - ownTop.current) < 1) return
    if (el.scrollTop + el.clientHeight < el.scrollHeight - TOLERANCE) setFollow(false)
  }

  return (
    <div className="absolute inset-0 flex flex-col" data-panel="log">
      <div className="flex shrink-0 items-center justify-end gap-4 px-7 py-2 font-mono text-[10px] tracking-[0.14em] uppercase">
        {log.error === null && (
          <span
            role="status"
            className="flex items-center gap-1.5 text-paper-50"
            data-stream={log.live ? 'streaming' : 'closed'}
          >
            {log.live && (
              <span
                aria-hidden="true"
                className="inline-block size-1.5 animate-pulse rounded-full bg-accent-line"
              />
            )}
            {log.live ? 'Streaming' : 'Stream closed'}
          </span>
        )}
        <button
          type="button"
          aria-label="Follow log"
          aria-pressed={follow}
          onClick={() => setFollow((f) => !f)}
          className={cn(
            'cursor-pointer border px-2.5 py-1 font-mono text-[10px] tracking-[0.14em] uppercase',
            follow
              ? 'border-accent-line bg-accent-line text-ink'
              : 'border-paper-35 bg-transparent text-paper-50',
          )}
        >
          Follow {follow ? 'on' : 'off'}
        </button>
      </div>
      {log.error !== null && (
        <div className="shrink-0 px-7 pb-2">
          <QuantUnavailable message={log.error} onRetry={log.retry} />
        </div>
      )}
      <pre
        ref={preRef}
        role="log"
        aria-label={ariaLabel}
        onScroll={onScroll}
        onWheel={(event) => {
          if (event.deltaY < 0) setFollow(false)
        }}
        className="m-0 min-h-0 flex-1 overflow-auto bg-ink px-6 py-3.5 font-mono text-[12px] leading-[1.65] whitespace-pre-wrap text-paper-90 [overflow-wrap:anywhere]"
      >
        {log.text}
      </pre>
    </div>
  )
}
